<script setup lang="ts">
import { usePlanAnalysisState } from "@/stores/PlanAnalysisState";
import { Z_INDEX_MODAL } from "@/components/canvas/layouts/constants";
import AnalysisProgressSteps from "./AnalysisProgressSteps.vue";

/**
 * Animation d'analyse : un trait lumineux violet balaye l'écran pendant tout
 * le pipeline (regroupement local + passes IA), avec le journal des étapes en
 * bas du plan. Plein écran et BLOQUANT :
 * l'outil dessin n'est pas utilisable pendant l'analyse (le scan lit le plan,
 * un tracé ou une suppression en vol fausserait handles et mesures).
 */

const state = usePlanAnalysisState();
</script>

<template>
  <div
    v-if="state.isAnalyzing"
    class="analysis-scan-overlay"
    :style="{ zIndex: Z_INDEX_MODAL - 100 }"
    @touchstart.prevent
    @pointerdown.stop.prevent
    @wheel.prevent
  >
    <div class="scan-beam" />
    <AnalysisProgressSteps />
  </div>
</template>

<style lang="scss" scoped>
.analysis-scan-overlay {
  // Plein écran, par-dessus barres d'outils et volets (sous les modales) :
  // tout toucher s'arrête ici tant que l'analyse tourne.
  position: fixed;
  inset: 0;
  overflow: hidden;
  cursor: progress;
  touch-action: none;
  // Léger voile violet : signale l'analyse sans masquer le plan.
  background: color-mix(in srgb, var(--dr-report) 6%, transparent);
}

.scan-beam {
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  width: 3px;
  background: var(--dr-report-light);
  box-shadow:
    0 0 12px 2px color-mix(in srgb, var(--dr-report-light) 80%, transparent),
    0 0 32px 8px color-mix(in srgb, var(--dr-report) 45%, transparent);
  animation: analysis-scan-sweep 2.4s ease-in-out infinite alternate;

  // Traînée du faisceau, côté opposé au sens de balayage.
  &::before {
    content: "";
    position: absolute;
    top: 0;
    bottom: 0;
    right: 3px;
    width: 120px;
    background: linear-gradient(
      to left,
      color-mix(in srgb, var(--dr-report) 22%, transparent),
      transparent
    );
  }
}

@keyframes analysis-scan-sweep {
  from {
    left: -2%;
  }
  to {
    left: 101%;
  }
}
</style>
